import {
  documentoVacio,
  type CVDocument,
  type Estudio,
  type Experiencia,
  type Idioma,
  type Otro,
  type Telefono,
} from "./model";
import { normalizarDetalles } from "./contextoInclusion";
import { ordenarExperiencias } from "./ordenar";

/**
 * De la respuesta de Directus (`?fields=*.*.*.*.*`) al documento.
 *
 * Port de `src/adapters/CurriculumVitaeAdapter.ts` y sus siete adapters
 * hermanos, con dos cambios deliberados:
 *
 *   1. Las fechas quedan en "YYYY-MM" y punto. El adapter viejo las pasa por
 *      `new Date()` y `toISOString()`, y ahi es donde una fecha de marzo se
 *      guarda como febrero: la conversion aplica la zona horaria del navegador
 *      a una fecha que no tiene hora. Aqui se leen del texto, sin `Date`.
 *
 *   2. Se conserva el id de la fila pivote (`recordId`). El builder viejo lo
 *      pierde y por eso no puede desvincular una experiencia sin releer el CV.
 */

/** Directus devuelve algunos campos como {value} o {code} segun la profundidad. */
export const normalizarValor = (val: unknown): unknown => {
  if (val == null) return val;
  if (Array.isArray(val)) return val.map(normalizarValor);

  if (typeof val === "object" && !(val instanceof Date)) {
    const o = val as Record<string, unknown>;
    // El telefono tiene estructura propia y no se desenvuelve.
    if (o.countryCode || o.areaCode || o.phoneNumber) return val;
    if (o.value !== undefined) return o.value;
    if (o.code !== undefined) return o.code;
    if (o.name !== undefined) return o.name;
    if (o.id !== undefined) return val;
    return String(val);
  }

  return val;
};

const texto = (val: unknown): string => {
  const v = normalizarValor(val);
  return v == null ? "" : String(v);
};

const listaDeTextos = (val: unknown): string[] => {
  const v = normalizarValor(val);
  if (!Array.isArray(v)) return [];
  return v.map((x) => (x == null ? "" : String(x))).filter((x) => x !== "");
};

const booleano = (val: unknown): boolean => {
  if (typeof val === "boolean") return val;
  if (typeof val === "number") return val === 1;
  if (typeof val === "string") {
    return ["true", "1", "si", "sí", "yes"].includes(val.trim().toLowerCase());
  }
  return false;
};

/**
 * Cualquier fecha de Directus a "YYYY-MM".
 *
 * Se lee del texto a proposito: `new Date("2024-03")` da la medianoche UTC, y
 * en Colombia (UTC-5) eso es el 29 de febrero a las 19:00. Formatear ESO en
 * local devuelve "2024-02" y la experiencia retrocede un mes cada vez que se
 * abre y se guarda.
 */
export const aMesAnio = (val: unknown): string | null => {
  if (val == null) return null;
  const s = String(val).trim();
  if (s === "") return null;

  // "2024-03", "2024-03-01", "2024-03-01T05:00:00.000Z"
  const m = s.match(/^(\d{4})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}`;

  // "2024" a secas: se asume enero, igual que hace el backend al importar.
  const soloAnio = s.match(/^(\d{4})$/);
  if (soloAnio) return `${soloAnio[1]}-01`;

  return null;
};

const telefono = (val: unknown): Telefono | null => {
  if (val == null || val === "") return null;
  if (typeof val === "object") {
    const o = val as Partial<Telefono>;
    return o.value ? { code: o.code ?? "", value: o.value } : null;
  }
  try {
    const parsed = JSON.parse(String(val)) as Partial<Telefono>;
    return parsed?.value ? { code: parsed.code ?? "", value: parsed.value } : null;
  } catch {
    // Telefonos viejos guardados como texto plano, antes de que fuera JSON.
    return { code: "", value: String(val) };
  }
};

const enlaces = (cv: Record<string, any>): string[] => {
  const pl = cv.profile_link;
  if (pl && typeof pl === "object" && Array.isArray(pl.links)) {
    return pl.links.filter(Boolean).map(String);
  }
  // Modelo antiguo: dos columnas sueltas en vez de la relacion.
  return [cv.profile_link, cv.profile_link_2].filter(Boolean).map(String);
};

const experiencias = (filas: unknown): Experiencia[] => {
  if (!Array.isArray(filas)) return [];
  return filas
    .filter((f) => f?.professional_experience_id)
    .map((f): Experiencia => {
      const e = f.professional_experience_id;
      return {
        id: Number(e.id),
        recordId: f.id != null ? Number(f.id) : undefined,
        company_name: texto(e.company_name),
        position: texto(e.position),
        sector: texto(e.sector),
        start_date: aMesAnio(e.start_date),
        end_date: booleano(e.current_job) ? null : aMesAnio(e.end_date),
        current_job: booleano(e.current_job),
        details: texto(e.details),
        achievements: texto(e.achievements),
      };
    });
};

const estudios = (filas: unknown): Estudio[] => {
  if (!Array.isArray(filas)) return [];
  return filas
    .filter((f) => f?.education_id)
    .map((f): Estudio => {
      const e = f.education_id;
      return {
        id: Number(e.id),
        recordId: f.id != null ? Number(f.id) : undefined,
        institution_name: texto(e.institution_name),
        title_name: texto(e.title_name),
        start_year: texto(e.start_year),
        year: texto(e.year),
        is_current: booleano(e.is_current),
        format: texto(e.format),
        country_name: texto(e.country_name),
        formation_level: texto(e.formation_level),
      };
    });
};

const idiomas = (filas: unknown): Idioma[] => {
  if (!Array.isArray(filas)) return [];
  return filas
    .filter((f) => f?.langs_id)
    .map((f): Idioma => ({
      id: Number(f.langs_id.id),
      recordId: f.id != null ? Number(f.id) : undefined,
      name: texto(f.langs_id.name),
      level: texto(f.langs_id.level),
    }));
};

const otros = (filas: unknown): Otro[] => {
  if (!Array.isArray(filas)) return [];
  return filas
    .filter((f) => f?.others_id)
    .map((f): Otro => ({
      id: Number(f.others_id.id),
      recordId: f.id != null ? Number(f.id) : undefined,
      section_name: texto(f.others_id.section_name),
      section_text: texto(f.others_id.section_text),
    }));
};

export const fromDirectus = (crudo: unknown): CVDocument => {
  // El endpoint devuelve un array de un elemento cuando se filtra por id.
  const cv = (Array.isArray(crudo) ? crudo[0] : crudo) as Record<string, any> | undefined;
  if (!cv || cv.id == null) return documentoVacio(0);

  const base = documentoVacio(Number(cv.id), texto(cv.session_id));

  return {
    ...base,
    name: texto(cv.name),
    last_names: texto(cv.last_names),
    email: texto(cv.email),
    phone: telefono(cv.phone),
    city: texto(cv.city),
    open_to_remote: booleano(cv.open_to_remote ?? cv.remote_open ?? cv.is_open_to_remote),
    profession: texto(cv.profession),
    age: cv.age != null && cv.age !== "" ? Number(cv.age) : null,
    links: enlaces(cv),

    resume: texto(cv.resume),
    experience: ordenarExperiencias(experiencias(cv.professional_experience)),
    education: estudios(cv.education),
    skills: listaDeTextos(cv.skills?.skill_name),
    soft_skills: listaDeTextos(cv.soft_skills?.skill_name),
    tools: listaDeTextos(cv.tools?.tools_name),
    languages: idiomas(cv.langs),
    others: otros(cv.others),

    is_inclusive: booleano(cv.is_inclusive),
    disability_type: cv.disability_type ? texto(cv.disability_type) : null,
    disability_certificate: booleano(cv.disability_certificate),
    disability_details: normalizarDetalles(cv.disability_details),

    meta: {
      skillsId: cv.skills?.id != null ? Number(cv.skills.id) : null,
      softSkillsId: cv.soft_skills?.id != null ? Number(cv.soft_skills.id) : null,
      toolsId: cv.tools?.id != null ? Number(cv.tools.id) : null,
      profileLinkId: cv.profile_link?.id != null ? Number(cv.profile_link.id) : null,
    },
  };
};
